"use client";

import React, { useMemo } from "react";
import HomeCitiesSelect from "./HomeCitiesSelect";
import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/routing";
import ListingCarouselItem from "@/components/listing/ListingCarouselItem";
import SavedListingsProvider from "@/components/listing/SavedListingsProvider";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { ListingSearchData } from "@/lib/types/search";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { getTopCitiesSlugNames } from "@/lib/data/location/data";

type Props = {
  locale: string;
};

const HomeLatestListings: React.FC<Props> = ({ locale }) => {
  const t = useTranslations("home.latest");
  const searchParams = useSearchParams();

  const cityOptions = useMemo(
    () =>
      getTopCitiesSlugNames(5, locale).map((city) => ({
        value: city.canonicalName,
        label: city.name,
      })),
    [locale],
  );

  const currentCity = useMemo(() => {
    const city = searchParams.get("city");

    return city && cityOptions.some((option) => option.value === city)
      ? city
      : (cityOptions[0]?.value ?? "");
  }, [cityOptions, searchParams]);
  const currentCityLabel =
    cityOptions.find((city) => city.value === currentCity)?.label ??
    currentCity;

  const { data, isLoading } = useQuery({
    queryKey: ["home", "latest-listings", currentCity, locale],
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("locale", locale);
      params.append("page", "1");
      params.append("pageSize", "10");
      params.append("sort", "date-desc");
      if (currentCity) params.append("city", currentCity);
      params.append("type", "APARTMENT");
      params.append("type", "HOUSE");
      params.append("atLeastOneImage", "true");

      const response = await fetch(
        `/api/site/public/listing?${params.toString()}`,
        {
          method: "GET",
        },
      );

      const body = (await response.json()) as {
        success: boolean;
        data: { items: ListingSearchData[]; count: number };
      };

      if (!response.ok || !body.success) {
        return { items: [], count: 0 };
      }

      return body.data;
    },
  });

  return (
    <div className="flex w-full flex-col gap-6 sm:gap-8">
      <div className="flex w-full flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div className="flex flex-row flex-wrap items-center gap-x-3 gap-y-2">
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            {t("title")}
          </h2>
          <HomeCitiesSelect
            options={cityOptions}
            current={currentCity}
            ariaLabel={t("city-select-label")}
          />
        </div>

        <Button asChild variant="secondary" className="h-auto min-h-11 w-fit whitespace-normal py-2 text-center sm:h-auto sm:min-h-10">
          <Link
            href={`/search?city=${currentCity}`}
            title={t("see-all-title", { name: currentCityLabel })}
          >
            {t("see-all", {
              name: currentCityLabel,
            })}
          </Link>
        </Button>
      </div>

      <div className="relative">
        {/* pb-7 fully contains the cards' hover shadow (extends ~22px); the
            negative margin keeps the section rhythm unchanged. */}
        <div className="overflow-x-auto scrollbar-none px-1 pt-1 pb-7 -mb-3">
          <div className="flex gap-2 px-4">
            {isLoading ? (
              <Skeletons />
            ) : (
              <ListingView items={data?.items || []} />
            )}
          </div>
        </div>

        <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-background to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent" />
      </div>
    </div>
  );
};

export default HomeLatestListings;

const Skeletons = () => {
  return (
    <>
      {[...Array(10)].map((_, i) => (
        <Skeleton
          key={`skeleton-${i}`}
          className="h-96 w-[300px] lg:w-96 mr-4 flex-shrink-0"
        />
      ))}
    </>
  );
};

const ListingView: React.FC<{ items: ListingSearchData[] }> = ({ items }) => {
  return (
    <SavedListingsProvider listingIds={items.map((item) => item.id)}>
      <>
        {items.map((listing) => (
          <div
            key={listing.href}
            className="min-w-[280px] max-w-[280px] lg:min-w-[320px] lg:max-w-[320px] mr-4 flex-shrink-0"
          >
            <ListingCarouselItem item={listing} className="h-full" />
          </div>
        ))}
      </>
    </SavedListingsProvider>
  );
};
